import React, { type ReactNode, useContext, useEffect } from 'react';

import Link from 'next/link';

import AllInboxIcon from '@mui/icons-material/AllInbox';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import HomeIcon from '@mui/icons-material/Home';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import PortraitSharpIcon from '@mui/icons-material/PortraitSharp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SettingsIcon from '@mui/icons-material/Settings';
import SourceIcon from '@mui/icons-material/Source';
import StarsSharpIcon from '@mui/icons-material/StarsSharp';
import {
  Badge,
  type BadgeProps,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  styled,
  Typography,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';

import { useFeatureFlags } from '@client/components/hooks/useFeatureFlags';
import { useAuth } from '@client/context/auth/AuthContext';
import { useOnboardingData } from '@client/context/onboarding/hooks/useOnboardingData';
import { usePersonalTaxData } from '@client/context/personalTax/hooks/usePersonalTaxInfo';
import TransactionContext from '@client/context/transactions/TransactionContext';
import useToggleIntercom from '@client/helpers/intercom/hooks/useToggleIntercom';
import { navigationPaths } from '@constants/navigation';
import { type SidebarListItemType, type SidebarListProps } from '@customTypes/navigationTypes';
import { type CompanyInfo } from '@customTypes/userInfo';
import isCompanyCheck from '@server/apiHelpers/validations/isCompanyCheck';
import { colors } from '@styles/theme';

import { getUnreconciledTransactionsBadgeColor } from '@app/frontend/lib/navigation/helpers';

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    // position: "absolute",
    right: 30,
    top: 0,
    // border: `1px solid ${theme.palette.background.paper}`,
    color: 'white',
    backgroundColor: 'red',
    // padding: "0 4px",
  },
}));

const SidebarListItem = (props: SidebarListItemType) => {
  return (
    <Link style={{ textDecoration: 'none' }} href={props.path} passHref legacyBehavior>
      <ListItemButton
        component={MuiLink}
        selected={props.path === location.pathname}
        disableGutters
        dense
        target={props.target}
        sx={props.listItemSxProps}
        onClick={props.onSidebarItemClicked}
        data-track-id={props.dataTrackId}>
        <ListItem sx={{ position: 'relative' }}>
          {props.badgeCount && props.badgeCount > 0 ? (
            <StyledBadge badgeContent={props.badgeCount} color='primary'>
              <ListItemIcon>{props.icon}</ListItemIcon>
            </StyledBadge>
          ) : (
            <ListItemIcon>{props.icon}</ListItemIcon>
          )}
          {/* {!props.badgeCount && <ListItemIcon>{props.icon}</ListItemIcon>} */}
          <ListItemText primary={props.name} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </ListItemButton>
    </Link>
  );
};

const SidebarList = (props: SidebarListProps) => {
  const subheaderId: string = `${props.id}-sidebar-menu`;
  return (
    <List
      subheader={
        <ListSubheader component='div' id={subheaderId}>
          <Typography
            variant='subtitle1'
            color='primary.dark'
            fontSize={'1.1rem'}
            sx={{ mb: 2, mt: 2 }}>
            {props.subheader}
          </Typography>
        </ListSubheader>
      }>
      {props.children}
    </List>
  );
};

const NestedList = ({
  icon,
  children,
  title,
  paths = [],
  collapsedText,
}: {
  icon: ReactNode;
  children: ReactNode;
  title: string;
  paths?: string[];
  collapsedText?: string;
}) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (paths) {
      if (paths.includes(location.pathname)) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, []);

  return (
    <>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ variant: 'body2' }}
          secondary={collapsedText}
          secondaryTypographyProps={{
            noWrap: true,
            fontSize: 12,
            lineHeight: open ? 0 : '16px',
            color: open ? colors.background.paper : colors.grey[500],
          }}
          primary={title}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
};

{
  /* -------COMPONENT------- */
}
const SidebarMenuList = () => {
  const { toggleIntercom } = useToggleIntercom();
  const { mainMenu, intake, aangiften, help } = navigationPaths;
  const {
    isVisible: isOnboardingItemVisible,
    companyInfo,
    intakeCorporationNotCompleted,
    isIntakeDoneByAstro,
  } = useOnboardingData();
  const { isVisible: isPersonalTaxItemVisible } = usePersonalTaxData();
  // const { missingDocuments } = useMissingDocumentsData();
  const { user } = useAuth();

  const featureFlags = useFeatureFlags(user?.uid);

  const { unreconciledTransactionCount } = useContext(TransactionContext);

  return (
    <>
      {/* DOC: Will be shown at onboarding or incorporation process only. */}

      {(isOnboardingItemVisible ||
        (isIntakeDoneByAstro &&
          isCompanyCheck(companyInfo as CompanyInfo) &&
          intakeCorporationNotCompleted)) && (
        // isCompanyCheck(companyInfo as CompanyInfo) && (
        <SidebarList subheader={'Klant worden'} id='klant-worden-onboarding'>
          {intakeCorporationNotCompleted && isCompanyCheck(companyInfo as CompanyInfo) && (
            <SidebarListItem
              name={'Oprichting'}
              path={intake.companySetup}
              icon={<AllInboxIcon />}
              dataTrackId='personal-tax'
            />
          )}

          {isOnboardingItemVisible && (
            <SidebarListItem
              name={'Onboarding'}
              path={intake.onboardingOverview}
              icon={<FolderSharedOutlinedIcon />}
              dataTrackId='onboarding-overview'
            />
          )}
        </SidebarList>
      )}

      {isPersonalTaxItemVisible && (
        <SidebarList subheader={'Aangiftes'} id='aangiften'>
          <SidebarListItem
            name={'Personenbelasting'}
            path={aangiften.personalTaxOverview}
            icon={<AllInboxIcon />}
            dataTrackId='personal-tax'
          />
        </SidebarList>
      )}

      {/* -------Main menu------- */}
      <SidebarList subheader={'Hoofdmenu'} id='main-menu'>
        <SidebarListItem
          name={'Home'}
          path={mainMenu.home}
          icon={<HomeIcon />}
          dataTrackId='home'
        />
        <SidebarListItem
          name={'Cijfers'}
          path={mainMenu.cijfers}
          icon={<BarChartIcon />}
          dataTrackId='cijfers'
        />

        <NestedList
          icon={<ContactPageOutlinedIcon />}
          title={'Advies'}
          paths={[mainMenu.advies, mainMenu.fiscalProfile]}>
          <SidebarListItem
            name={'Adviezen'}
            path={mainMenu.advies}
            icon={<StarsSharpIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='advice'
          />
          <SidebarListItem
            name={'Fiscaal profiel'}
            path={mainMenu.fiscalProfile}
            icon={<PortraitSharpIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='fiscal-profile'
          />
        </NestedList>

        <NestedList
          icon={<ReceiptIcon />}
          title={'Facturatie'}
          paths={[mainMenu.verkoop, mainMenu.makeNewInvoice, mainMenu.invoiceSettings]}
          // collapsedText={"Alle facturen, Nieuwe factuur"}
        >
          <SidebarListItem
            name={'Alle facturen'}
            path={mainMenu.verkoop}
            icon={<ReceiptLongOutlinedIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='invoice-overview'
          />
          <SidebarListItem
            name={'Nieuwe factuur'}
            path={mainMenu.makeNewInvoice}
            icon={<NoteAddOutlinedIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='new-invoice'
          />
          <SidebarListItem
            name={'Factuurinstelling'}
            path={mainMenu.invoiceSettings}
            icon={<SettingsIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='invoice-settings'
          />
        </NestedList>

        <NestedList
          icon={<RequestQuoteIcon />}
          title={"Onkostennota's"}
          paths={[mainMenu.expenseReport, mainMenu.makeNewExpenseReport]}>
          <SidebarListItem
            name={"Alle onkostennota's"}
            path={mainMenu.expenseReport}
            icon={<ReceiptLongOutlinedIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='expense-reports-overview'
          />
          <SidebarListItem
            name={'Nieuwe onkostennota'}
            path={mainMenu.makeNewExpenseReport}
            icon={<NoteAddOutlinedIcon />}
            listItemSxProps={{ pl: 2 }}
            dataTrackId='new-expense-report'
          />
        </NestedList>
        <SidebarListItem
          name={'Documenten'}
          path={mainMenu.documentOverview}
          icon={<SourceIcon />}
        />

        {featureFlags?.transactiesEnabled && (
          <SidebarListItem
            name={'Bank transacties'}
            path={mainMenu.transactions}
            icon={
              <Badge
                badgeContent={unreconciledTransactionCount}
                color={getUnreconciledTransactionsBadgeColor(unreconciledTransactionCount)}>
                <PaymentsIcon />
              </Badge>
            }
          />
        )}
      </SidebarList>

      {/* //-------Help------- */}
      <SidebarList subheader={'Hulp'} id='help'>
        <SidebarListItem
          name={'Helpcenter'}
          path={help.helpCenter}
          icon={<HelpCenterIcon />}
          target='_blank'
          dataTrackId='helpcenter'
        />

        <ListItemButton
          onClick={() => toggleIntercom()}
          sx={{ position: 'relative', width: '100%' }}
          disableGutters
          dense
          data-track-id='chat-accountant'>
          <ListItem sx={{ position: 'relative' }}>
            <ListItemIcon>{<ChatBubbleOutlineOutlinedIcon />}</ListItemIcon>

            <ListItemText
              primary={'Chat met je accountant'}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </ListItemButton>
      </SidebarList>
    </>
  );
};

export default SidebarMenuList;
